import type { AxiosInstance } from 'axios'
import type { H3Event } from 'h3'
import type { ComputedRef } from 'vue'
import type { NuxtApp } from '#app'
import type { HomeShowcasesNormalized, HomeShowcasesPayload } from '~/utils/homeShowcases'
import {
  cloneHomeShowcases,
  DEFAULT_HOME_SHOWCASES_FALLBACK_FILE,
  EMPTY_HOME_SHOWCASES,
  mergeHomeShowcases,
} from '~/utils/homeShowcases'
import { loadFallbackJsonPayload } from '~/utils/helpers'
import { CachedPageUtils } from '~/utils/CachedPageUtils'

export type UseHomeShowcasesDataReturn = {
  homeShowcases: ComputedRef<HomeShowcasesNormalized>
}

/**
 * Charge le fallback JSON puis fusionne la réponse API `/api/home-showcases`.
 * @param {AxiosInstance} api - Client HTTP API.
 * @param {string} fallbackFile - Fichier ou URL fallback (`NUXT_PUBLIC_HOME_SHOWCASES_FALLBACK_URL`).
 * @param {string} requestOrigin - Origin SSR / client pour le fetch static.
 * @param {H3Event | undefined} requestEvent - Requête en cours côté serveur, absente dans le navigateur.
 * @returns {Promise<HomeShowcasesNormalized>} Cartes showcase fusionnées JSON + API.
 */
async function fetchHomeShowcasesMerged(
  api: AxiosInstance,
  fallbackFile: string,
  requestOrigin: string,
  requestEvent: H3Event | undefined,
): Promise<HomeShowcasesNormalized> {
  const jsonPayload: HomeShowcasesPayload | null =
    await loadFallbackJsonPayload<HomeShowcasesPayload>(
      fallbackFile,
      DEFAULT_HOME_SHOWCASES_FALLBACK_FILE,
      requestOrigin,
    )

  let apiPayload: HomeShowcasesPayload | null = null
  try {
    const { data } = await api.get<HomeShowcasesPayload>('/api/home-showcases', { timeout: 8000 })
    apiPayload = data ?? null
  } catch (error: unknown) {
    CachedPageUtils.reportFailedApiCall('/api/home-showcases', error, requestEvent)
    apiPayload = null
  }

  const fromJson: HomeShowcasesNormalized = mergeHomeShowcases(
    cloneHomeShowcases(EMPTY_HOME_SHOWCASES),
    jsonPayload,
  )
  return mergeHomeShowcases(fromJson, apiPayload)
}

/**
 * Cartes showcase de la home — JSON fallback (`public/`) puis patch API.
 * @returns {UseHomeShowcasesDataReturn} `homeShowcases` calculé depuis JSON + patch API.
 */
export function useHomeShowcasesData(): UseHomeShowcasesDataReturn {
  const config: ReturnType<typeof useRuntimeConfig> = useRuntimeConfig()
  const api: AxiosInstance = useFrontApi()
  const homeShowcasesFallbackFile: string = config.public.homeShowcasesFallbackFile
  const requestOrigin: string = useRequestURL().origin
  const requestEvent: H3Event | undefined = useRequestEvent()

  const { data: homeShowcasesMerged } = useAsyncData<HomeShowcasesNormalized>(
    'home-showcases',
    (): Promise<HomeShowcasesNormalized> =>
      fetchHomeShowcasesMerged(api, homeShowcasesFallbackFile, requestOrigin, requestEvent),
    {
      server: true,
      lazy: false,
      default: () => cloneHomeShowcases(EMPTY_HOME_SHOWCASES),
      getCachedData(key: string, nuxtApp: NuxtApp): HomeShowcasesNormalized | undefined {
        const cached: HomeShowcasesNormalized | undefined = nuxtApp.payload.data[key] as
          | HomeShowcasesNormalized
          | undefined
        if (
          import.meta.client &&
          cached &&
          cached.schools?.length === 0 &&
          cached.professions?.length === 0 &&
          cached.articles?.length === 0
        ) {
          return undefined
        }
        return cached
      },
    },
  )

  const homeShowcases: ComputedRef<HomeShowcasesNormalized> = computed<HomeShowcasesNormalized>(
    (): HomeShowcasesNormalized =>
      homeShowcasesMerged.value ?? cloneHomeShowcases(EMPTY_HOME_SHOWCASES),
  )

  return { homeShowcases }
}
